/**
 * Turnstile helpers: request tokens and manage short-lived session cache.
 */

import { TURNSTILE_CONFIG } from '@config/turnstile'

const SESSION_STORAGE_KEY = 'rocketshipreaders.turnstile.session'
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

type SessionCache = {
  token: string
  expiresAt: number
}

let scriptPromise: Promise<void> | null = null
let widgetId: string | null = null
let pendingResolve: ((token: string) => void) | null = null
let pendingReject: ((error: Error) => void) | null = null
let tokenQueue: Promise<void> = Promise.resolve()

function loadScript(): Promise<void> {
  if (scriptPromise) {
    return scriptPromise
  }

  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Turnstile unavailable'))
      return
    }

    if (window.turnstile) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Turnstile script'))
    document.head.appendChild(script)
  })

  return scriptPromise
}

function getOrCreateContainer(): HTMLElement {
  const existing = document.getElementById('turnstile-container')
  if (existing) {
    return existing
  }

  const container = document.createElement('div')
  container.id = 'turnstile-container'
  container.style.position = 'absolute'
  container.style.left = '-9999px'
  container.style.width = '1px'
  container.style.height = '1px'
  document.body.appendChild(container)
  return container
}

async function ensureWidget(): Promise<string> {
  if (widgetId) {
    return widgetId
  }

  await loadScript()

  if (!window.turnstile) {
    throw new Error('Turnstile not available')
  }

  const siteKey = TURNSTILE_CONFIG.SITE_KEY
  if (!siteKey) {
    throw new Error('Turnstile site key not configured')
  }

  const container = getOrCreateContainer()
  widgetId = window.turnstile.render(container, {
    sitekey: siteKey,
    size: 'invisible',
    callback: (token: string) => {
      if (widgetId) {
        window.turnstile?.reset(widgetId)
      }
      if (pendingResolve) {
        pendingResolve(token)
      }
      pendingResolve = null
      pendingReject = null
    },
    'error-callback': () => {
      if (widgetId) {
        window.turnstile?.reset(widgetId)
      }
      if (pendingReject) {
        pendingReject(new Error('Turnstile error'))
      }
      pendingResolve = null
      pendingReject = null
    },
    'expired-callback': () => {
      if (widgetId) {
        window.turnstile?.reset(widgetId)
      }
      if (pendingReject) {
        pendingReject(new Error('Turnstile expired'))
      }
      pendingResolve = null
      pendingReject = null
    },
  })

  return widgetId
}

async function requestTurnstileToken(): Promise<string> {
  const task = tokenQueue.then(
    () =>
      new Promise<string>(async (resolve, reject) => {
        try {
          const id = await ensureWidget()
          pendingResolve = resolve
          pendingReject = reject
          window.turnstile?.execute(id)
        } catch (error) {
          pendingResolve = null
          pendingReject = null
          reject(error instanceof Error ? error : new Error('Turnstile error'))
        }
      })
  )

  tokenQueue = task.then(
    () => undefined,
    () => undefined
  )

  return task
}

function readSessionCache(): SessionCache | null {
  if (typeof window === 'undefined') {
    return null
  }

  const raw = localStorage.getItem(SESSION_STORAGE_KEY)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as SessionCache
    if (!parsed.token || !parsed.expiresAt) {
      return null
    }
    if (parsed.expiresAt <= Date.now()) {
      localStorage.removeItem(SESSION_STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeSessionCache(token: string): void {
  if (typeof window === 'undefined') {
    return
  }
  const ttl = TURNSTILE_CONFIG.SESSION_TTL_MS
  const expiresAt = Date.now() + (Number.isFinite(ttl) ? ttl : 0)
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ token, expiresAt }))
}

export function storeTurnstileSessionFromResponse(response: Response): void {
  const token = response.headers.get('X-Turnstile-Session')
  if (token) {
    writeSessionCache(token)
  }
}

export async function getTurnstileHeaders(): Promise<Record<string, string>> {
  const cached = readSessionCache()
  if (cached?.token) {
    return { 'X-Turnstile-Session': cached.token }
  }

  const token = await requestTurnstileToken()
  return { 'X-Turnstile-Token': token }
}
